import { TAG_TYPES } from '@/lib/api/tags';
import {
  buildListQueryParams,
  handledError,
  processListPage,
  processServerError,
} from '@/lib/api/listQuery';
import { notifyOnSuccess } from '@/lib/api/notifyOnSuccess';
import { SUPPLIER_SEARCH_IN, SUPPLIER_SORT_FIELDS } from '../constants/suppliers.constants';
import {
  buildSupplierDto,
  processSupplierForm,
  processSupplierRow,
} from '../mappers/suppliersMappers';

const PATHS = {
  list: '/suppliers',
  supplier: (id) => `/suppliers/${id}`,
};

const listTag = { type: TAG_TYPES.Supplier, id: 'LIST' };

export const suppliersEndpoints = (build) => ({
  // Named getAccountSuppliers, not getSuppliers — that name is already taken by lib/api/lookupsApi.js.
  getAccountSuppliers: build.query({
    query: (params) => ({
      url: PATHS.list,
      params: buildListQueryParams(params, {
        sortFields: SUPPLIER_SORT_FIELDS,
        searchIn: SUPPLIER_SEARCH_IN,
      }),
    }),
    transformResponse: (dto) => processListPage(dto, 'suppliers', processSupplierRow),
    providesTags: (result) =>
      result
        ? [...result.rows.map((row) => ({ type: TAG_TYPES.Supplier, id: row.id })), listTag]
        : [listTag],
  }),

  getSupplierForm: build.query({
    query: (id) => ({ url: PATHS.supplier(id) }),
    transformResponse: (dto) => processSupplierForm(dto?.output?.supplier),
    transformErrorResponse: handledError,
    providesTags: (result, error, id) => [{ type: TAG_TYPES.Supplier, id }],
  }),

  createSupplier: build.mutation({
    query: (values) => ({
      url: PATHS.list,
      method: 'POST',
      body: buildSupplierDto(values),
    }),
    transformErrorResponse: processServerError,
    onQueryStarted: notifyOnSuccess('SUPPLIERS_SAVED'),
    invalidatesTags: [listTag],
  }),

  // PUT, not PATCH — fully replaces the supplier's shipping-sites collection, never a merge.
  updateSupplier: build.mutation({
    query: ({ id, values }) => ({
      url: PATHS.supplier(id),
      method: 'PUT',
      body: buildSupplierDto(values),
    }),
    transformErrorResponse: processServerError,
    onQueryStarted: notifyOnSuccess('SUPPLIERS_SAVED'),
    invalidatesTags: (result, error, arg) => [{ type: TAG_TYPES.Supplier, id: arg.id }, listTag],
  }),

  // No DELETE /suppliers/:id exists on the backend — suppliers can only be added/edited.
});
